import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import styles from "./MainContent.module.css";
import { StatsSection } from "./StatsSection";
import { PortfolioSection } from "./PortofolioSection";
import { PerformanceSection } from "./PerformanceSection";
import { FeedbackSection } from "./FeedbackSection";
import AdsComponents from "../ads/AdsComponents";
import StepSection from "./StepSection";
import BotSection from "./BotSection";
import ExchangeWalletSection from "./ExchangeWallet";
import TableDashboard from "./TableHeaderDashboard";

import { baseAxios } from "../../apis";
import { getRandomColor } from "../../utils";
import { useBot } from "../../context/botContext";
import SplashScreen from "../../SplashScreen";
import DialogTour from "../dialogs/DialogTour";
import { dashboardTourSteps } from "../../data/tourData";
import { useAuth } from "../../context/authContext";

const useDashboardTableQuery = (selectedExchange) => {
  const exchangeId = selectedExchange?.id || null;
  const isAll = selectedExchange?.name === "All Exchange";

  return useQuery({
    queryKey: ["dashboardTable", exchangeId],
    enabled: Boolean(selectedExchange && (exchangeId || isAll)),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    retry: false,
    queryFn: async () => {
      const params = exchangeId ? { exchange_id: exchangeId } : {};
      const { data } = await baseAxios.get("/api/dashboard-table/", { params });

      return {
        ...data,
        best_pairs: data?.best_pairs?.map((pr) => ({
          ...pr,
          color: getRandomColor(),
        })),
      };
    },
  });
};

export const MainContent = () => {
  const { user } = useAuth();
  const { exchanges, myExchanges, loadingGetExchange } = useBot();
  const [openDialog, setOpenDialog] = useState(null);

  const [hasConnectedExchange, setHasConnectedExchange] = useState(true);
  const [selectedExchange, setSelectedExchange] = useState(null);

  const { data: dashboard, isLoading: loadingDashboard } =
    useDashboardTableQuery(selectedExchange);

  //  Load splash only on first login (controlled via localStorage)
  const [showSplash, setShowSplash] = useState(() => {
    return !localStorage.getItem("dashboardLoaded");
  });

  // Set default exchange
  useEffect(() => {
    if (!selectedExchange && exchanges?.length) {
      setSelectedExchange(exchanges[0]);
    }
  }, [exchanges, selectedExchange]);

  // When data loads for the first time, mark as loaded
  useEffect(() => {
    if (!loadingDashboard && !loadingGetExchange) {
      localStorage.setItem("dashboardLoaded", "true");
      setShowSplash(false);
    }
  }, [loadingDashboard, loadingGetExchange, dashboard]);

  // 🔥 Control Dashboard Tour visibility (backend + localStorage)
  useEffect(() => {
    // Wait until user is available
    if (!user) return;

    // If backend says tutorial/tourtip is disabled globally, never show
    if (user.is_tourtip === true) return;

    // If localStorage already disabled this dashboard tour, do not show it
    const isDashboardTourDisabled =
      localStorage.getItem("tour_disabled_dashboard") === "true";

    if (!isDashboardTourDisabled) {
      setOpenDialog("open-tour-dashboard");
    }
  }, [user]);

  useEffect(() => {
    const handler = () => setOpenDialog("open-tour-dashboard");
    window.addEventListener("open-dashboard-tour", handler);
    return () => window.removeEventListener("open-dashboard-tour", handler);
  }, []);

  //  Show splash only first time after login
  if (showSplash) {
    return <SplashScreen />;
  }

  return (
    <div className="space-y-6">
      <DialogTour
        open={openDialog === "open-tour-dashboard"}
        onClose={() => setOpenDialog(null)}
        steps={dashboardTourSteps}
        tourKey="dashboard"
      />

      <StepSection dashboard={dashboard} myExchanges={myExchanges} />

      <ExchangeWalletSection
        exchanges={exchanges}
        myExchanges={myExchanges}
        loading={loadingGetExchange}
      />

      <BotSection />

      <StatsSection
        dashboard={dashboard}
        exchanges={exchanges}
        selectedExchange={selectedExchange}
        setSelectedExchange={setSelectedExchange}
        loading={loadingDashboard}
      />

      {!hasConnectedExchange ? (
        <div className={styles.exchangeHandler}>
          <h3 className={styles.exchangeTitle}>Available Exchange</h3>
          <div className={styles.exchangeGrid}>
            {Array.from({ length: 15 }).map((_, i) => (
              <div key={i} className={styles.exchangeCard}>
                <div className={styles.cardTitle}>
                  <div className={styles.exchangeIcon}></div>
                  <div className={styles.exchangeName}>Exchange {i + 1}</div>
                </div>
                <div className={styles.buttons}>
                  <button className={styles.fastConnectButton}>
                    Fast Connect
                  </button>
                  <button className={styles.apiButton}>Connect with API</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <>
          <PortfolioSection dashboard={dashboard} loading={loadingDashboard} />
        </>
      )}

      <TableDashboard dashboard={dashboard} loading={loadingDashboard} />
      <PerformanceSection dashboard={dashboard} loading={loadingDashboard} />

      <div className="flex flex-col lg:flex-row lg:space-x-6 mb-5 gap-5 mt-6 lg:gap-0">
        <FeedbackSection />
        <div className="w-full lg:w-1/2 bg-white rounded-xl max-h-[150px]">
          <AdsComponents />
        </div>
      </div>
    </div>
  );
};
